/**
 * Icon button that opens the help-request dialog for the current SIM level.
 *
 * @module components/helpdesk/HelpRequestButton
 * @author Snowfly
 * @version 1.0.0
 */
import {
    IconButton,
    Tooltip
} from "@material-ui/core";
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import React, {useState} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {createHelpdeskTicket, getPresignedUrl, uploadToPresignedUrl} from "../../services/result.service";
import {useLocation} from "react-router-dom";
import helpdeskPermissions from "../../permissions/helpdesk";
import permissionCheck from "../../utils/permissionCheck";
import {HelpRequestDialog} from "./HelpRequestDialog";
import useAlert from "../common/UseAlert";

const useQuery = () => new URLSearchParams(useLocation().search);

/**
 * HelpRequestButton is a React functional component that allows users to submit a help request,
 * including an optional message and file attachments. It triggers a dialog where the user can enter
 * details about their request and manage attachments. Upon submitting, it creates a helpdesk ticket
 * through a backend service.
 *
 * Props:
 * - simData (Object): Contains simulation-related data such as simulation ID, level ID, and step information.
 * - classes (Object): Contains style-related class names to customize the appearance of the component.
 *
 * State:
 * - helpDialogOpen (boolean): Manages the visibility state of the help dialog.
 * - page (string): Tracks the current page location from the router.
 * - helpText (string): Stores the user's help request message.
 * - attachments (Array): Stores the finalized list of uploaded attachments linked to the ticket.
 * - fileAttachments (Array): Temporarily holds file attachments awaiting processing and upload.
 *
 * Behavior:
 * - Tracks changes to fileAttachments and processes uploads to generate presigned URLs using
 *   a backend API. Uploaded attachments are stored in the attachments state.
 * - Listens to changes in the current router location (path) to update the `page` state variable.
 * - Manages dialog state for opening, closing, and submitting via handlers `handleClick`,
 *   `handleCloseDialog`, and `handleAttachments`.
 * - Performs permission checks for creating helpdesk tickets via the `permissionCheck` method.
 * - Uses `setAlert` to display success or error notifications to the user during various operations.
 *
 * Returns:
 * - A button styled with an icon and tooltip prompting the user to request help.
 * - A conditional dialog component (`HelpRequestDialog`) for entering help request information
 *   and managing attachments, rendered only when `helpDialogOpen` is true.
 */
const HelpRequestButton = ({
    simData,
    classes,
}) => {
    const location = useLocation();
    const query = useQuery();
    const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
    const [page, setPage] = React.useState('');
    const [helpText, setHelpText] = React.useState("");
    const [attachments, setAttachments] = useState([]);
    const [fileAttachments, setFileAttachments] = useState([]);
    const {setAlert} = useAlert();

    React.useEffect(
        () => {
            setPage(location.pathname);
        },
        [location]
    );

    React.useEffect(
        () => {
            /**
             * Asynchronously updates the attachments by processing a list of file attachments.
             * It generates a unique ID for each attachment, uploads files to a presigned URL,
             * and updates their status. Successfully uploaded attachments are appended to the
             * list of new attachments. Alerts are set to notify the user about success or failure.
             *
             * This function uploads files only if their respective attachment URLs are not already
             * defined. For each file, it fetches presigned URLs for uploading and downloading. If
             * the upload succeeds, it updates the attachment's properties, adds them to the new
             * attachments list, and updates the application's state.
             *
             * Steps performed:
             * 1. Creates a copy of the current attachments.
             * 2. Iterates through the list of file attachments.
             * 3. For attachments without a URL:
             *    - Generates a unique identifier.
             *    - Gets presigned URLs for upload and download.
             *    - Uploads the file to the presigned upload URL.
             *    - Updates the attachment's state and progress based on upload success or failure.
             * 4. Updates the state with the new list of attachments if modifications were made.
             *
             * @async
             * @function
             * @returns {Promise<void>} Resolves when the function completes the update operation.
             */
            const updateAttachments = async () => {
                let newAttachments = attachments.slice(0);
                let processed = false;

                for await (const [fileIndex, attachment] of fileAttachments.entries()) {
                    if (!attachment.url) {
                        let attachment_id = window.crypto.randomUUID();
                        let signedUrls =  await getPresignedUrl('attachment', attachment_id);
                        if (signedUrls && signedUrls.success) {
                            attachment.status = 'uploading';
                            let uploaded = false;
                            try {
                                uploaded = await uploadToPresignedUrl(signedUrls.presignedUrl, attachment.file);
                            } catch (err) {
                                setAlert('Could not upload attachment', 'error');
                            }
                            if (uploaded && uploaded.success) {
                                processed = true;
                                attachment.status = 'uploaded';
                                attachment.progress = 100;
                                newAttachments.push(
                                    {
                                        attachment_id: attachment_id,
                                        file: {
                                            name: attachment.file.name,
                                            type: attachment.file.type,
                                            size: attachment.file.size,
                                        },
                                        url: signedUrls.downloadUrl,
                                    }
                                );
                                fileAttachments[fileIndex].url = signedUrls.downloadUrl;
                                setAlert('Attachment uploaded successfully.', 'success');
                            } else {
                                attachment.status = 'error';
                                setAlert('Failed to upload attachment', 'error');
                            }
                        }
                    }
                }

                if (processed) {
                    setAttachments(newAttachments);
                }

                //console.log('Set attachments to = %o', newAttachments);
            };

            updateAttachments();
        },
        [fileAttachments]
    );

    /**
     * Event handler function for handling click events.
     * This function is triggered when a click event occurs and sets the
     * help dialog state to open by updating the relevant state property.
     *
     * @param {Object} evt - The event object associated with the click event.
     */
    const handleClick = (/* evt */) => {
        setHelpDialogOpen(true);
    };

    /**
     * Handles the processing of file attachments by updating the state accordingly.
     *
     * @async
     * @function handleAttachments
     * @param {Array} fileAttachments - An array representing the file attachments to be processed.
     * This is used to update the current state to include the provided file attachments.
     * @returns {Promise<void>} A promise that resolves once the attachments have been processed.
     */
    const handleAttachments = async (fileAttachments) => {
        setFileAttachments(fileAttachments);
    };

    /**
     * Handles the close event for a dialog. This function performs specific actions based
     * on the element triggering the event and manages the state of the dialog after processing.
     * If the triggering element has the ID 'sendButton', it generates a help request object
     * using relevant data, sends it to the backend to create a Helpdesk ticket, and provides
     * feedback to the user based on the result of the operation.
     *
     * @param {Object} evt - The event object representing the user's action.
     * @param {HTMLElement} evt.currentTarget - The DOM element that triggered the event.
     * @param {string} evt.currentTarget.id - The ID of the triggering element.
     */
    const handleCloseDialog = async (evt) => {
        if (evt.currentTarget.id === 'sendButton') {
            let helpRequest = {
                sim_id: simData?.sim_id,
                level_id: simData?.level?._id ?? simData?.level,
                script: simData?.step,
                message: helpText,
                page: page,
                attachments: attachments,
            };
            if (query.get("assignment_id")) {
                helpRequest.assignment_id = query.get("assignment_id");
            }
            //console.log('Help Request: %o', helpRequest);

            // Create the helpdesk ticket in the backend
            let result = await createHelpdeskTicket(helpRequest);

            if (result && result.success) {
                setAlert('The Helpdesk ticket was created successfully.', 'success');
            } else {
                setAlert('The Helpdesk ticket was NOT created.', 'error');
                console.error(result);
            }
        }
        setHelpDialogOpen(false);
        setHelpText('');
    };

    if (!permissionCheck(helpdeskPermissions.tickets.create.task, helpdeskPermissions.tickets.create.type)) {
        return (<></>);
    }

    return (
        <>
            <Tooltip title="Request Help">
                <span>
                    <IconButton onClick={handleClick} color="primary">
                      <LiveHelpIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <HelpRequestDialog dialogOpen={helpDialogOpen}
                               onCloseDialog={handleCloseDialog}
                               helpText={helpText}
                               setHelpText={setHelpText}
                               attachments={attachments}
                               setAttachments={setAttachments}
                               handleAttachments={handleAttachments}
                               classes={classes}
            />
        </>
    )
}

HelpRequestButton.propTypes = {
    simData: PropTypes.object,
    classes: PropTypes.object,
};

export default connect(null)(HelpRequestButton);
